import type {
  AccountLoginRequest,
  AccountProfile,
  AccountToken,
  CreateIAMUserRequest,
  CreatePersonalAccessTokenRequest,
  CreatePersonalAccessTokenResponse,
  DeleteIAMUserRequest,
  DeletePersonalAccessTokenRequest,
  GetIAMUserRequest,
  IamUser,
  IamUserListItem,
  ListIAMUsersData,
  ListIAMUsersRequest,
  ListIAMUsersResponse,
  ListPersonalAccessTokenRequest,
  ListPersonalAccessTokenResponse,
  PersonalAccessToken,
  SetPersonalAccessTokenActiveStatusRequest,
  UserNameMapResponse,
} from '@/types/iam.types';
import { iamClient } from './request';

/**
 * 权限策略搜索接口的原生查询参数。
 *
 * IAM 服务要求分页值使用字符串，并直接识别 snake_case 参数；这里保持网络契约原样，
 * 避免 API 层再引入一套页面分页模型。
 */
export type SearchPoliciesOrderField = 'created_at' | 'statement_count' | 'authorization_count';

/** 权限授权支持的主体类型。 */
export type IamPolicyGrantIdentityType = 'user_group' | 'user';

export interface SearchPoliciesParams {
  limit: string;
  offset: string;
  order_by: SearchPoliciesOrderField | `-${SearchPoliciesOrderField}`;
  search?: string;
}

/** 按授权主体查询权限策略时，在搜索参数基础上指定主体类型与 ID。 */
export interface SearchIdentityPoliciesParams extends SearchPoliciesParams {
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
}

/** 权限策略搜索接口返回的单条原生记录。 */
export interface IamPolicySearchRecord {
  name: string;
  policy_id: string;
  description: string;
  statement_count: number;
  action_count: number;
  type: 'system' | 'custom';
  authorization_count: number;
  created_at: number;
}

/** 按授权主体查询的策略记录；is_grant 标识该主体是否已经获得此策略。 */
export interface IamIdentityPolicySearchRecord extends IamPolicySearchRecord {
  is_grant: boolean;
}

/** 权限策略搜索接口的原生响应；字段命名与 IAM 服务返回保持一致。 */
export interface SearchPoliciesResponse {
  code: number;
  message: string;
  data: {
    total: number;
    policies: IamPolicySearchRecord[];
  };
}

/** 按授权主体查询权限策略的原生响应。 */
export interface SearchIdentityPoliciesResponse {
  code: number;
  message: string;
  data: {
    total: number;
    policies: IamIdentityPolicySearchRecord[];
  };
}

/** 权限资源分组接口返回的单个资源。 */
export interface IamActionResource {
  display_name: string;
  service: string;
  resource: string;
}

/** 权限资源分组；group 用于展示，group_value 用于选择值和后续请求。 */
export interface IamActionResourceGroup {
  group: string;
  group_value: string;
  items: IamActionResource[];
}

/** 权限资源分组接口的原生响应。 */
export interface ListActionResourcesResponse {
  code: number;
  message: string;
  data: {
    list: IamActionResourceGroup[];
  };
}

/** 权限点接口返回的单条原生记录。 */
export interface IamActionRecord {
  code: string;
  display_name: string;
  service_name: string;
  /** 展示标签。 */
  group: string;
  /** 稳定值，用于筛选、分组与后续请求。 */
  group_value: string;
  subcategory: string;
  operation_type: string;
  is_dangerous: boolean;
  description: string;
}

export interface IamActionCollection {
  total: number;
  actions: IamActionRecord[];
}

/** 按资源分组查询权限点接口的原生响应。 */
export interface ListActionsResponse {
  code: number;
  message: string;
  data: {
    total: number;
    write_actions: IamActionCollection;
    read_items: IamActionCollection;
    list_items: IamActionCollection;
    permission_items: IamActionCollection;
  };
}

/** 策略配置中的原生 Statement 结构。 */
export interface IamPolicyConfigStatement {
  effect: string;
  action: string[];
  condition?: {
    string_equals?: Record<string, string>;
  };
}

/** 创建权限策略的原生请求；字段命名与 IAM 服务契约保持一致。 */
export interface CreatePolicyRequest {
  name: string;
  description: string;
  policy_config: {
    version: '1';
    statement: IamPolicyConfigStatement[];
  };
}

/** 更新权限策略的原生请求；保存接口相较创建接口额外要求 policy_id。 */
export interface UpdatePolicyRequest extends CreatePolicyRequest {
  policy_id: string;
}

/** 策略详情中权限点规则的原生结构。 */
export interface IamPolicyPermissionAction {
  code: string;
  display_name: string;
  service_name: string;
  /** 展示标签。 */
  group: string;
  /** 稳定值，用于筛选和分组。 */
  group_value: string;
  operation_type: string;
  description: string;
  decision: {
    allowed: boolean;
    range: string;
    reason: string;
  };
  rules: Array<{
    effect: string;
    range: string;
  }>;
}

/** 权限策略详情接口的原生响应；API 层不拆解或重组其中的策略配置。 */
export interface GetPolicyResponse {
  code: number;
  message: string;
  data: {
    policy_id: string;
    name: string;
    description: string;
    created_at: number;
    authorization_count: number;
    type: 'system' | 'custom';
    policy_config: {
      version: string;
      statement: IamPolicyConfigStatement[];
    };
    permission_summary: {
      action_total: number;
      actions: IamPolicyPermissionAction[];
    };
  };
}

/** 策略授权记录中的资源范围。 */
export interface IamPolicyGrantRange {
  display_name: string;
  service: string;
  resource: string;
  range: string;
}

/** 授权列表接口的原生查询参数。 */
export interface SearchPolicyGrantsParams {
  identity_type?: IamPolicyGrantIdentityType;
  limit: number;
  offset: string;
  policy_id?: string;
  policy_type?: string;
  search?: string;
  order_by?: 'authorization_at' | '-authorization_at';
}

/** 策略授权记录接口返回的单条原生记录。 */
export interface IamPolicyGrantRecord {
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
  identity_name: string;
  policy_name: string;
  policy_id: string;
  authorization_at: number;
  authorization_range: IamPolicyGrantRange[];
  policy_description?: string;
  authorizer_name: string;
  type: 'system' | 'custom';
}

/** 查询策略授权记录的原生响应。 */
export interface SearchPolicyGrantsResponse {
  code: number;
  message: string;
  data: {
    total: number;
    records: IamPolicyGrantRecord[];
  };
}

/** 授权详情接口的原生查询参数。 */
export interface GetPolicyGrantParams {
  identity: string;
  identity_type: IamPolicyGrantIdentityType;
  policy_id: string;
}

/** 授权详情接口返回的授权记录。 */
export interface IamPolicyGrantDetailRecord {
  grant_id: number;
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
  identity_name: string;
  policy_id: string;
  policy_name: string;
  policy_description: string;
  policy_type: 'system' | 'custom';
  authorizer_type: string;
  authorizer: string;
  authorizer_name: string;
  authorization_at: number;
}

/** 授权详情接口返回的单个权限点及其最终判定。 */
export interface IamPolicyGrantPermissionAction {
  code: string;
  display_name: string;
  service_name: string;
  group: string;
  subcategory: string;
  operation_type: string;
  is_dangerous: boolean;
  description: string;
  decision: {
    allowed: boolean;
    range: string;
    reason: string;
  };
  rules: Array<{
    effect: string;
    range: string;
  }>;
}

/** 授权详情接口的原生响应。 */
export interface GetPolicyGrantResponse {
  code: number;
  message: string;
  data: {
    grant_record: IamPolicyGrantDetailRecord;
    permission_summary: {
      action_total: number;
      actions: IamPolicyGrantPermissionAction[];
    };
  };
}

/** 用户权限汇总中的策略授权来源。 */
export interface IamUserPolicyGrantSource {
  grant_id: number;
  source_type: 'direct' | 'group_inherited';
  range: string;
  effect: string;
  policy_id: string;
  policy_name: string;
  action_total: number;
  resource_total: number;
  service_total: number;
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
  identity_name: string;
}

/** 用户权限接口返回的权限点。授权记录中的 actions 不含 decision/source。 */
export interface IamUserPolicyAction {
  code: string;
  display_name: string;
  service_name: string;
  group: string;
  group_value: string;
  subcategory: string;
  operation_type: string;
  is_dangerous: boolean;
  description: string;
  rules?: Array<{
    effect: string;
    range: string;
  }>;
  decision?: {
    allowed: boolean;
    range: string;
    reason: string;
  };
  source?: IamUserPolicyGrantSource[];
}

/** 用户的直接授权或用户组继承授权记录。 */
export interface IamUserPolicyGrantRecord {
  grant_id: number;
  source_type: 'direct' | 'group_inherited';
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
  identity_name: string;
  policy_id: string;
  policy_name: string;
  policy_description: string;
  policy_type: 'system' | 'custom';
  authorizer_type: string;
  authorizer: string;
  authorizer_name: string;
  authorization_at: number;
  action_total: number;
  actions: IamUserPolicyAction[];
}

export interface IamUserPolicyGrantCollection {
  grant_total: number;
  action_total: number;
  records: IamUserPolicyGrantRecord[];
}

/** GET /v1/policy/grant/user 的原生响应。 */
export interface GetUserPolicyGrantsResponse {
  code: number;
  message: string;
  data: {
    total: number;
    user_total: number;
    group_total: number;
    actions: IamUserPolicyAction[];
    direct_authorization: IamUserPolicyGrantCollection;
    group_inheritance: IamUserPolicyGrantCollection;
    permission_summary: {
      action_total: number;
      actions: IamUserPolicyAction[];
    };
  };
}

/** 用户组列表的请求契约。 */
export interface ListIAMUserGroupsRequest {
  page: number;
  limit: number;
  name: string;
  display_name: string;
  keyword: string;
  order_by?: string[];
}

/** 用户组列表中 edges.users 返回的原生成员记录。 */
export type IamUserGroupMember = IamUserListItem;

/** 用户组列表记录的关联数据；无成员时服务端可能返回空对象。 */
export interface IamUserGroupEdges {
  users?: IamUserGroupMember[];
}

/** 用户组列表返回的单条原生记录。 */
export interface IamUserGroupRecord {
  id: string;
  created_at: string;
  updated_at?: string;
  domain_id: string;
  name: string;
  display_name: string;
  comment?: string;
  edges: IamUserGroupEdges;
}

/** 用户组列表响应中的数据。 */
export interface ListIAMUserGroupsData {
  items: IamUserGroupRecord[];
  totalCount: number;
}

/** ListUserGroups 的原生响应。 */
export interface ListIAMUserGroupsResponse {
  code: number;
  message: string;
  data: ListIAMUserGroupsData;
}

/** 获取用户组详情的请求。 */
export interface GetIAMUserGroupRequest {
  id: string;
}

/** GetUserGroup 的原生响应包络。 */
export interface GetIAMUserGroupResponse {
  code: number;
  message: string;
  data: IamUserGroupRecord;
}

/** GetUser 的 edges.groups 返回的原生用户组详情。 */
export interface IamUserDetailGroupRecord {
  id: string;
  created_at: string;
  updated_at: string;
  domain_id: string;
  name: string;
  display_name: string;
  comment: string;
  edges: Record<string, unknown>;
  joined_at: string;
}

/** GetUser 关联数据；无所属用户组时服务端可能返回空对象。 */
export interface IamUserDetailEdges {
  groups?: IamUserDetailGroupRecord[];
}

/** GetUser 响应 data 中的原生用户详情。 */
export interface IamUserDetailRecord {
  id: string;
  created_at: string;
  updated_at: string;
  domain_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  email_verified: boolean;
  phone_verified: boolean;
  enabled: boolean;
  enable_console_login: boolean;
  enable_openapi: boolean;
  edges: IamUserDetailEdges;
  last_login_at: string;
}

/** POST /v1/GetUser 的原生响应。 */
export interface GetIAMUserResponse {
  code: number;
  message: string;
  data: IamUserDetailRecord;
}

/** 创建用户组的原生请求。 */
export interface CreateIAMUserGroupRequest {
  name: string;
  display_name: string;
  comment: string;
}

/** 更新用户组的原生请求；users 当前按 IAM 服务要求传成员 name 集合。 */
export interface UpdateIAMUserGroupRequest {
  id: string;
  display_name: string;
  comment: string;
  users: string[];
}

/** 调整用户组成员的原生请求。 */
export interface AddUserToGroupRequest {
  group_id: string;
  add_users: string[];
  remove_users: string[];
}

/** 将单个用户批量加入多个用户组的原生请求。 */
export interface BatchAddUserToGroupRequest {
  user_id: string;
  add_group_ids: string[];
}

/** 删除用户组的原生请求。 */
export interface DeleteIAMUserGroupRequest {
  group_id: string;
}

export interface GrantPolicyIdentity {
  identity_type: IamPolicyGrantIdentityType;
  identity: string;
}

/** 为一批用户或用户组授予一批策略。 */
export interface GrantPolicyRequest {
  policy_ids: string[];
  identities: GrantPolicyIdentity[];
}

/** 撤销用户或用户组的一批策略授权，请求结构与授权接口一致。 */
export type RevokePolicyGrantRequest = GrantPolicyRequest;

/** 无响应数据的 IAM 写操作原生响应。 */
export interface IamOperationResponse {
  code: number;
  message: string;
}

export const iamApi = {
  getAccountToken: async (data: AccountLoginRequest): Promise<AccountToken> => {
    const res = await iamClient.post<AccountToken>('/v1/GetAccountToken', {
      ...data,
      user_type: data.user_type ?? 'account',
    });
    return res.data;
  },

  getAccountProfile: async (accessToken: string): Promise<AccountProfile> => {
    const res = await iamClient.post<AccountProfile>(
      '/v1/GetAccountProfile',
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return res.data;
  },

  listIAMUsers: async (params: ListIAMUsersRequest): Promise<ListIAMUsersData> => {
    const { page = 1, limit = 20, name, display_name, keyword, order_by } = params;
    const body: Record<string, unknown> = { page, limit };
    if (name !== undefined) body.name = name;
    if (display_name !== undefined) body.display_name = display_name;
    if (keyword !== undefined) body.keyword = keyword;
    if (order_by?.length) body.order_by = order_by;
    const res = await iamClient.post<ListIAMUsersResponse>('/v1/ListUsers', body);
    if (res.data.code !== 0) {
      throw new Error(res.data.message || '获取用户列表失败');
    }
    return res.data.data;
  },

  listIAMUserGroups: async (data: ListIAMUserGroupsRequest): Promise<ListIAMUserGroupsData> => {
    const res = await iamClient.post<ListIAMUserGroupsResponse>('/v1/ListUserGroups', data);
    if (res.data.code !== 0) {
      throw new Error(res.data.message || '获取用户组列表失败');
    }
    return res.data.data;
  },

  /** 获取用户组详情；校验 IAM 业务码后返回 data 中的原生用户组记录。 */
  getIAMUserGroup: async (data: GetIAMUserGroupRequest): Promise<IamUserGroupRecord> => {
    const res = await iamClient.post<GetIAMUserGroupResponse>('/v1/GetUserGroup', data);
    if (res.data.code !== 0) {
      throw new Error(res.data.message || '获取用户组详情失败');
    }
    return res.data.data;
  },

  /** 创建用户组。 */
  createIAMUserGroup: async (data: CreateIAMUserGroupRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.post<IamOperationResponse>('/v1/CreateUserGroup', data);
    return res.data;
  },

  /** 更新用户组。 */
  updateIAMUserGroup: async (data: UpdateIAMUserGroupRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.post<IamOperationResponse>('/v1/UpdateUserGroup', data);
    return res.data;
  },

  /** 调整用户组成员。 */
  addUserToGroup: async (data: AddUserToGroupRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.post<IamOperationResponse>('/v1/AddUserToGroup', data);
    return res.data;
  },

  /** 将单个用户批量加入多个用户组。 */
  batchAddUserToGroup: async (data: BatchAddUserToGroupRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.post<IamOperationResponse>('/v1/BatchAddUserToGroup', data);
    return res.data;
  },

  deleteIAMUserGroup: async (data: DeleteIAMUserGroupRequest): Promise<void> => {
    await iamClient.post('/v1/DeleteUserGroup', data);
  },

  listUsersName: async (): Promise<UserNameMapResponse> => {
    const res = await iamClient.post<UserNameMapResponse>('/v1/ListUserNameMap', {});
    const d = res.data;
    return {
      userNames: d.userNames ?? {},
    };
  },

  /** 获取用户详情及其关联用户组；该路由由平台 API 代理。 */
  getIAMUser: async (req: GetIAMUserRequest): Promise<GetIAMUserResponse> => {
    const res = await iamClient.post<GetIAMUserResponse>('/v1/GetUser', req);
    return res.data;
  },

  createIAMUser: async (data: CreateIAMUserRequest): Promise<IamUser> => {
    const res = await iamClient.post<IamUser>('/v1/CreateUser', data);
    return res.data;
  },

  deleteIAMUser: async (data: DeleteIAMUserRequest): Promise<void> => {
    await iamClient.post('/v1/DeleteUser', data);
  },

  submitSAMLResponse: async (samlResponse: string): Promise<AccountToken> => {
    const formData = new URLSearchParams();
    formData.append('SAMLResponse', samlResponse);
    const res = await iamClient.post<AccountToken>('/v1/saml/callback', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return res.data;
  },

  // Personal Access Token
  createPersonalAccessToken: async (
    data: CreatePersonalAccessTokenRequest
  ): Promise<CreatePersonalAccessTokenResponse> => {
    const res = await iamClient.post<CreatePersonalAccessTokenResponse>(
      '/v1/CreatePersonalAccessToken',
      data
    );
    return res.data;
  },

  listPersonalAccessTokens: async (
    params?: ListPersonalAccessTokenRequest
  ): Promise<ListPersonalAccessTokenResponse> => {
    const res = await iamClient.post<ListPersonalAccessTokenResponse>(
      '/v1/ListPersonalAccessToken',
      params ?? {}
    );
    return res.data;
  },

  deletePersonalAccessToken: async (data: DeletePersonalAccessTokenRequest): Promise<void> => {
    await iamClient.post('/v1/DeletePersonalAccessToken', data);
  },

  setPersonalAccessTokenActiveStatus: async (
    data: SetPersonalAccessTokenActiveStatusRequest
  ): Promise<PersonalAccessToken> => {
    const res = await iamClient.post<PersonalAccessToken>(
      '/v1/SetPersonalAccessTokenActiveStatus',
      data
    );
    return res.data;
  },

  /** 搜索权限策略；响应保持 IAM 服务的原生结构，由使用方按需读取。 */
  searchPolicies: async (params: SearchPoliciesParams): Promise<SearchPoliciesResponse> => {
    const res = await iamClient.get<SearchPoliciesResponse>('/v1/policy/search', { params });
    return res.data;
  },

  /** 按授权主体查询权限策略，并返回每条策略的已有授权状态。 */
  searchIdentityPolicies: async (
    params: SearchIdentityPoliciesParams
  ): Promise<SearchIdentityPoliciesResponse> => {
    const res = await iamClient.get<SearchIdentityPoliciesResponse>('/v1/policy/user', { params });
    return res.data;
  },

  /** 获取创建策略时可选择的服务分组。 */
  listActionResources: async (): Promise<ListActionResourcesResponse> => {
    const res = await iamClient.get<ListActionResourcesResponse>('/v1/action/resource/list');
    return res.data;
  },

  /** 根据选中的服务分组获取权限点。 */
  listActions: async (groupValue: string): Promise<ListActionsResponse> => {
    const res = await iamClient.get<ListActionsResponse>('/v1/action/list', {
      params: { group_value: groupValue },
    });
    return res.data;
  },

  /** 创建权限策略。 */
  createPolicy: async (data: CreatePolicyRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.post<IamOperationResponse>('/v1/policy/create', data);
    return res.data;
  },

  /** 更新权限策略。 */
  updatePolicy: async (data: UpdatePolicyRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.put<IamOperationResponse>('/v1/policy/save', data);
    return res.data;
  },

  /** 删除指定权限策略；iamClient 的 baseURL 已包含 /api/iam。 */
  deletePolicy: async (policyId: string): Promise<IamOperationResponse | undefined> => {
    const res = await iamClient.delete<IamOperationResponse | undefined>(
      `/v1/policy/${encodeURIComponent(policyId)}`
    );
    return res.data;
  },

  /** 获取权限策略详情；不在 API 层转换策略 Statement 或权限摘要。 */
  getPolicy: async (policyId: string): Promise<GetPolicyResponse> => {
    const res = await iamClient.get<GetPolicyResponse>('/v1/policy', {
      params: { policy_id: policyId },
    });
    return res.data;
  },

  /** 查询授权记录；分页与筛选字段保持 IAM 服务的原生命名。 */
  searchPolicyGrants: async (
    params: SearchPolicyGrantsParams
  ): Promise<SearchPolicyGrantsResponse> => {
    const res = await iamClient.get<SearchPolicyGrantsResponse>('/v1/policy/grant/search', {
      params,
    });
    return res.data;
  },

  /** 获取单条授权详情；主体与策略共同确定唯一授权记录。 */
  getPolicyGrant: async (params: GetPolicyGrantParams): Promise<GetPolicyGrantResponse> => {
    const res = await iamClient.get<GetPolicyGrantResponse>('/v1/policy/grant', { params });
    return res.data;
  },

  /** 获取指定用户的直接授权、用户组继承授权及最终权限汇总。 */
  getUserPolicyGrants: async (identity: string): Promise<GetUserPolicyGrantsResponse> => {
    const res = await iamClient.get<GetUserPolicyGrantsResponse>('/v1/policy/grant/user', {
      params: { identity },
    });
    return res.data;
  },

  /** 为用户或用户组授权。 */
  grantPolicy: async (data: GrantPolicyRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.put<IamOperationResponse>('/v1/policy/grant/save', data);
    return res.data;
  },

  /** 撤销用户或用户组的策略授权。 */
  revokePolicyGrant: async (data: RevokePolicyGrantRequest): Promise<IamOperationResponse> => {
    const res = await iamClient.put<IamOperationResponse>('/v1/policy/grant/revoke', data);
    return res.data;
  },
};
